'use client'
import { Box, Hidden, SidebarNavItem, Text } from '@doist/marketist'

import { ContainerSidebar } from 'components/container-sidebar/container-sidebar'
import { useLocale } from 'next-intl'
import { useQueryState } from 'nuqs'

import { PostCard } from '../post-card/post-card'

import HandwrittenArrow from './handwritten-arrow.svg'

import css from './article-list-section.module.css'

import { useTranslation } from '@/app/i18n/client'
import { BlogIndex } from '@/app/services/contentful/schemas/contentful'
import { dynamicTranslate } from '@/services/productivity-methods/dynamic-translation'

type ArticleListSectionProps = {
    tags: BlogIndex['tags']
    items: BlogIndex['posts']
}

const ARTICLE_LIST_ANCHOR = 'posts-section'

export function ArticleListSection({ tags, items }: ArticleListSectionProps) {
    const locale = useLocale()
    const { t } = useTranslation(locale, 'productivity-methods')
    const { t: tPMCommon } = useTranslation(locale, 'pm-common')
    const [category, setCategory] = useQueryState('category')

    const categoryItems = items.filter((item) =>
        item.tagList?.find((tag) => tag?.slug === category),
    )
    const filteredItems =
        !category || category === 'all' || categoryItems.length === 0 ? items : categoryItems

    function handleCategoryClick(slug: string) {
        setCategory(slug)

        const container = document.querySelector(`#${ARTICLE_LIST_ANCHOR}`)
        container?.scrollIntoView()
    }

    return (
        <ContainerSidebar id={ARTICLE_LIST_ANCHOR} className={css.section}>
            <Hidden below="lg">
                <ContainerSidebar.Aside className={css.sidebar}>
                    <Text size="minor-base" weight="semibold" className="pbe-12">
                        {t('home.categories')}
                    </Text>
                    <ul>
                        <SidebarNavItem
                            active={!category || category === 'all'}
                            onClick={() => handleCategoryClick('all')}
                        >
                            {t('home.allMethods')}
                        </SidebarNavItem>
                        {tags.map((tag) => (
                            <SidebarNavItem
                                active={category === tag.slug}
                                key={tag.slug}
                                onClick={() => handleCategoryClick(tag.slug)}
                            >
                                {dynamicTranslate(tag.name, tPMCommon, locale)}
                            </SidebarNavItem>
                        ))}
                    </ul>
                </ContainerSidebar.Aside>
            </Hidden>
            <ContainerSidebar.Main className={css.main}>
                <Box className={css.handwrittenIllus}>
                    <HandwrittenArrow />
                    <Text size="handwritten-base">{t('home.explore')}</Text>
                </Box>
                {filteredItems.map((item) => (
                    <PostCard key={item.slug} post={item} onCategoryClick={handleCategoryClick} />
                ))}
            </ContainerSidebar.Main>
        </ContainerSidebar>
    )
}
